import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';
import { VerificationSession } from '../../../types/verification';
import * as verificationService from '../../../services/verificationService';

export interface VerificationSessions {
  sessions: VerificationSession[];
  sessionsLoading: boolean;
  sessionsError: string | null;
  selectedSession: VerificationSession | null;
  setSelectedSession: (session: VerificationSession | null) => void;
  detailLoading: boolean;
  loadSessions: () => Promise<void>;
  loadSessionDetail: (sessionId: string) => Promise<void>;
  /** Resolves the session the export dialog should show, loading it if the list row is a stub. */
  prepareExport: (sessionId: string) => Promise<boolean>;
}

export function useVerificationSessions(
  onError: (message: string) => void,
  options: { enabled?: boolean; onUnavailable?: () => void } = {},
): VerificationSessions {
  // Default true so existing callers are unchanged; the verification page passes
  // false while its tenant flag is off, to skip a request the gate would 404.
  const { enabled = true, onUnavailable } = options;
  // Held in a ref, not a dependency: an inline callback would otherwise rebuild
  // loadSessions every render and the mount effect would refetch forever.
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<VerificationSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // The failure is stored as its wire code, never as translated prose: the fetch
  // must not depend on `t`, or every language toggle refetches the whole list.
  const [sessionsFailure, setSessionsFailure] = useState<{ code?: string } | null>(null);
  const [selectedSession, setSelectedSession] = useState<VerificationSession | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsFailure(null);
    try {
      const response = await verificationService.listSessions({ limit: 50 });
      setSessions(response.sessions);
    } catch (err) {
      const code = (err as { code?: string } | null)?.code;
      // A 404 here means the tenant gate closed while this session was open.
      // Re-resolving the flag flips the page to its notice instead of leaving a
      // generic load error on screen.
      if (code === 'HTTP_404') onUnavailableRef.current?.();
      setSessionsFailure({ code });
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const sessionsError = useMemo(
    () =>
      sessionsFailure &&
      resolveApiErrorMessage(t, sessionsFailure, { fallbackKey: 'verification.sessionsLoadFailed' }),
    [sessionsFailure, t],
  );

  const loadSessionDetail = useCallback(
    async (sessionId: string) => {
      setDetailLoading(true);
      try {
        const session = await verificationService.getSession(sessionId);
        setSelectedSession(session);
      } catch (err) {
        onError(resolveApiErrorMessage(t, err, { fallbackKey: 'verification.sessionLoadFailed' }));
      } finally {
        setDetailLoading(false);
      }
    },
    [onError, t],
  );

  const prepareExport = useCallback(
    async (sessionId: string) => {
      const session = sessions.find((s) => s.id === sessionId) || selectedSession;
      if (!session) return false;
      // If the session doesn't have units loaded (list endpoint returns units: []),
      // load full detail first to avoid overwriting the currently loaded session
      if (!session.units || session.units.length === 0) {
        try {
          const fullSession = await verificationService.getSession(sessionId);
          setSelectedSession(fullSession);
        } catch {
          // Fall back to the list session if detail load fails
          setSelectedSession(session);
        }
      } else if (selectedSession?.id !== sessionId) {
        setSelectedSession(session);
      }
      return true;
    },
    [sessions, selectedSession],
  );

  useEffect(() => {
    if (!enabled) return;
    loadSessions();
  }, [enabled, loadSessions]);

  return {
    sessions,
    sessionsLoading,
    sessionsError,
    selectedSession,
    setSelectedSession,
    detailLoading,
    loadSessions,
    loadSessionDetail,
    prepareExport,
  };
}
